// @flow
import gql from 'graphql-tag';
import React, {Component} from 'react';
import classnames from 'classnames';
import {FormattedMessage} from 'react-intl';
import HitCarouselContainer from '../HitCarouselContainer/HitCarouselContainer';
import Price from '../Price/Price';
import Rating from '../Rating/Rating';
import {SleepsElement} from '../Details/Details';
import {MAP_PHOTOCAROUSEL_ADVANCE} from '../../utils/carouselEdapUtils';
import TripBoardButton from '../TripBoardButton/TripBoardButton';

import type {Shape as HitShape} from '../Hit/Hit';

import './MapHit.less';
import {Fragment} from 'redux-little-router';

export const fragments = {
    SERPHit: gql`
        fragment MapHitSERPHit on SERPHit {
            location {
                latitude
                longitude
            }
        }
    `
};

export type Shape = {
    location?: {
        latitude: string,
        longitude: string
    }
} & HitShape;

type Props = {
    hit: Shape,
    onHitClick?: (Shape) => void,
    locale: string
};

type State = {
    isLoading: boolean
};

type MapHitDetailsProps = {
    id: string,
    value: number
};

type MapHitBathroomsDetailProps = {
    bathroomsFull: number,
    bathroomsHalf: number,
    bathroomsToiletOnly: number
};

export const MapHitDetails = ({id, value}: MapHitDetailsProps) => (
    <div>
        <span className="MapHit__details--value">{value}</span>
        <span className="MapHit__details--label text-capitalize">
            <FormattedMessage
                id={id}
                description={`${id} label`}
                defaultMessage={`${id}`}
            />
        </span>
    </div>
);

export const MapHitBedroomsDetail = (props: MapHitDetailsProps) => (
    <div className="MapHit__bedrooms MapHit__details">
        {props.value > 0 ? (
            <span className="MapHit__details--label text-capitalize">
                <FormattedMessage
                    id="bedroomsAbbreviationParam"
                    description="bedrooms abbreviation label"
                    defaultMessage="BR"
                    values={{
                        bedrooms: props.value
                    }}
                >
                    {(txt) => <Fragment>{txt}</Fragment>}
                </FormattedMessage>
            </span>
        ) : (
            <FormattedMessage
                id="bedroomsStudioLabel"
                description="studio label"
                defaultMessage="Studio"
            />
        )}
    </div>
);

export const MapHitBathroomsDetail = ({
    bathroomsFull,
    bathroomsHalf,
    bathroomsToiletOnly
}: MapHitBathroomsDetailProps) => {
    if (bathroomsFull > 0) {
        return (
            <span className="MapHit__details--label text-capitalize">
                <FormattedMessage
                    id="bathroomsFullAbbreviationParam"
                    description={`bathrooms label`}
                    defaultMessage={`bathrooms`}
                    values={{
                        bathroomsFull
                    }}
                >
                    {(txt) => <Fragment>{txt}</Fragment>}
                </FormattedMessage>
            </span>
        );
    } else if (bathroomsHalf > 0) {
        return (
            <MapHitDetails
                id="bathroomsHalfAbbreviation"
                value={bathroomsHalf}
            />
        );
    } else if (bathroomsToiletOnly > 0) {
        return (
            <MapHitDetails
                id="bathroomsToiletOnlyAbbreviation"
                value={bathroomsToiletOnly}
            />
        );
    }
    return null;
};

/**
 * Typically rendered within a google maps infobox, which stops propagation on all
 * events. Sets up a ref on the main hit anchor element. No other DOM events
 * will work unless you create refs and wire them up manually.
 */
export default class MapHit extends Component<Props, State> {
    constructor() {
        super();
        this.state = {
            isLoading: true
        };
    }

    componentDidMount() {
        /* istanbul ignore else */
        if (this.linkRef.current) {
            this.linkRef.current.addEventListener('click', this.onClick);
        }
    }

    componentWillUnmount() {
        /* istanbul ignore else */
        if (this.linkRef.current) {
            this.linkRef.current.removeEventListener('click', this.onClick);
        }
    }

    // TODO: should be able to type this with something like `Ref<HTMLAnchorElement>`,
    // but Flow 0.74 complains. Try again when https://flow.org/en/docs/react/refs/
    // is updated to reflect new `React.createRef` API.
    linkRef: {current: null | HTMLAnchorElement} = React.createRef();

    onClick = () => {
        if (this.props.onHitClick) {
            this.props.onHitClick(this.props.hit);
        }
    };

    render() {
        const {hit} = this.props;
        const hasPrice =
            hit.priceSummary ||
            (hit.price && hit.price.value && hit.price.currencyUnits);
        const mapHitRatingClassName = classnames({
            'MapHit__info--priceRating-review': hasPrice,
            'MapHit__info--priceRating-review-noPrice': !hasPrice
        });

        return (
            <div className="MapHit" data-wdio="MapHit">
                <div
                    className="MapHit__carousel content--clickable"
                    data-wdio="MapHit__carousel"
                >
                    <div className="Hit__tripboardButton">
                        <TripBoardButton hit={hit} isMapHit />
                    </div>
                    <HitCarouselContainer
                        detailPageUrl={hit.detailPageUrl}
                        edapAction={MAP_PHOTOCAROUSEL_ADVANCE}
                        images={hit.images}
                        listingId={hit.listingId}
                        onImageClick={this.onClick}
                        totalImageCount={hit.imageCount}
                        simpleCarousel
                    />
                </div>
                <a
                    className={'MapHit__link a--plain-link'}
                    href={hit.detailPageUrl}
                    ref={this.linkRef}
                    target={'_blank'}
                >
                    <div className="MapHit__info">
                        <div className="MapHit__info--details">
                            <MapHitBedroomsDetail
                                id="bedroomsAbbreviation"
                                value={hit.details.bedrooms}
                            />
                            <MapHitBathroomsDetail
                                bathroomsFull={hit.details.bathroomsFull}
                                bathroomsHalf={hit.details.bathroomsHalf}
                                bathroomsToiletOnly={
                                    hit.details.bathroomsToiletOnly
                                }
                            />
                            <SleepsElement
                                value={hit.details.sleeps}
                                shouldHideDot
                            />
                        </div>
                        <div className="MapHit__info--priceRating">
                            <div className="MapHit__info--priceRating-price">
                                <Price
                                    price={hit.price}
                                    priceSummary={hit.priceSummary}
                                    useAverage
                                    useShort
                                    locale={hit.locale}
                                />
                            </div>
                            <div className={mapHitRatingClassName}>
                                <Rating
                                    averageRating={hit.averageRating}
                                    reviewCount={hit.reviewCount}
                                />
                            </div>
                        </div>
                    </div>
                </a>
            </div>
        );
    }
}
